/**
 * The per-message diff (docs/plans/lms-course-editor-plan.md 8.4, D10,
 * Appendix D, 18.4; WP-E30): what one applied message changed, computed once
 * on the server when the message was written, so later edits never change
 * what message 3 is shown to have done.
 *
 * - Sections: DiffView's DiffSectionCard for each changed, added or removed
 *   section (the diff's sections block is exactly diffContent's shape);
 *   unchanged sections are folded away unless asked for.
 * - Questions: per changed question its ordinal, section, the fields that
 *   changed and a word-level view of the stem, the options (one line each)
 *   and, when the server sends it, the explanation (18.4: typed optional).
 * - Course level: objectives, not covered, glossary terms added, removed or
 *   changed, takeaways, the checklist and the quiz settings, before and after.
 * - Swaps: each literal pair with its count. Sources: entries added (and
 *   removed) by the message's documents.
 *
 * Nothing is editable here: the admin edits the draft in the editor.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import type { DiffReadingLine } from '../../../../api/catalog';
import type { EditDiff, EditDiffCourseEntry, EditDiffQuestion } from '../../../../api/editor';
import type { ChecklistItem, CourseSource, GlossaryEntry } from '../../../../content/types';
import { pillCls } from '../../../../components/player/styles';
import { DiffSectionCard } from '../DiffView';
import { Checkbox } from '../fields';
import { cardCls, microLabelCls, subheadingCls } from '../styles';
import { checklistChanges, glossaryChanges, settingChanges, sourceChanges } from './editModel';

export interface EditDiffViewProps {
  diff: EditDiff;
}

/** 18.4: a question entry may carry the explanation's word diff beyond Appendix D. */
type DiffQuestion = EditDiffQuestion & { explanation_diff?: DiffReadingLine[] | null };

const QUESTION_STATUS: Record<string, { label: string; tone: 'green' | 'red' | 'amber' }> = {
  added: { label: 'Added', tone: 'green' },
  removed: { label: 'Removed', tone: 'red' },
  changed: { label: 'Changed', tone: 'amber' },
};

const COURSE_FIELD_LABELS: Record<string, string> = {
  learning_objectives: 'Learning objectives',
  not_covered: 'Not covered',
  glossary: 'Glossary',
  key_takeaways: 'Key takeaways',
  competency_checklist: 'Competency checklist',
  quiz_settings: 'Quiz settings',
  sources: 'Sources',
};

const FIELD_WORDS: Record<string, string> = {
  stem: 'stem',
  options: 'options',
  explanation: 'explanation',
  kind: 'kind',
  difficulty: 'difficulty',
  objective_index: 'objective',
  source_ref: 'source reference',
};

function plural(n: number, one: string, many?: string): string {
  return `${n} ${n === 1 ? one : many ?? `${one}s`}`;
}

/** "2 sections changed, 1 added, 3 slides, ..." (the diff's counts). */
export function countsSummary(diff: EditDiff): string {
  const c = diff.counts || ({} as EditDiff['counts']);
  const parts: string[] = [];
  if (c.sections_changed) parts.push(`${plural(c.sections_changed, 'section')} changed`);
  if (c.sections_added) parts.push(`${plural(c.sections_added, 'section')} added`);
  if (c.slides_changed) parts.push(plural(c.slides_changed, 'slide'));
  if (c.paragraphs_changed) parts.push(plural(c.paragraphs_changed, 'reading paragraph'));
  if (c.questions_changed) parts.push(plural(c.questions_changed, 'question'));
  if (c.course_fields_changed) parts.push(plural(c.course_fields_changed, 'course-level field'));
  if (c.swaps_applied) parts.push(plural(c.swaps_applied, 'wording swap'));
  return parts.length > 0 ? `${parts.join(', ')}.` : 'This message changed nothing in the draft.';
}

/** A word-level diff inline: inserted words underlined, removed words struck through. */
function WordDiff({ runs, testId }: { runs: DiffReadingLine[]; testId: string }) {
  if (!Array.isArray(runs) || runs.length === 0) return null;
  return (
    <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200" data-testid={testId}>
      {runs.map((r, i) => {
        const space = i > 0 ? ' ' : '';
        if (r.op === 'added') {
          return (
            <span key={i}>
              {space}
              <ins className="rounded bg-green-50 px-0.5 text-green-900 decoration-green-600 dark:bg-green-900/20 dark:text-green-200" data-op="added">
                <span className="sr-only">added: </span>
                {r.text}
              </ins>
            </span>
          );
        }
        if (r.op === 'removed') {
          return (
            <span key={i}>
              {space}
              <del className="rounded bg-red-50 px-0.5 text-red-900 decoration-red-500 dark:bg-red-900/20 dark:text-red-200" data-op="removed">
                <span className="sr-only">removed: </span>
                {r.text}
              </del>
            </span>
          );
        }
        return (
          <span key={i} data-op="same">
            {space}
            {r.text}
          </span>
        );
      })}
    </p>
  );
}

/** A line-level diff: one row per line with its word (Added, Removed, Same). */
function LineDiff({ lines, testId }: { lines: DiffReadingLine[]; testId: string }) {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  return (
    <ol className="space-y-1" data-testid={testId}>
      {lines.map((line, i) => (
        <li
          key={i}
          data-op={line.op}
          className={`whitespace-pre-line rounded-lg border px-3 py-1.5 text-sm ${
            line.op === 'added'
              ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200'
              : line.op === 'removed'
                ? 'border-red-200 bg-red-50 text-red-900 line-through decoration-red-400 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200'
                : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
          }`}
        >
          <span className="mr-2 text-[10px] font-semibold uppercase tracking-widest no-underline">
            {line.op === 'added' ? 'Added' : line.op === 'removed' ? 'Removed' : 'Same'}
          </span>
          {line.text}
        </li>
      ))}
    </ol>
  );
}

function QuestionCard({ q, sectionTitle }: { q: DiffQuestion; sectionTitle: string | null }) {
  const status = QUESTION_STATUS[q.status] ?? { label: q.status, tone: 'amber' as const };
  const number = q.ordinal ?? q.before_ordinal;
  const moved = q.ordinal !== null && q.before_ordinal !== null && q.ordinal !== q.before_ordinal;
  const fields = Array.isArray(q.changed_fields) ? q.changed_fields : [];
  const explanation = Array.isArray(q.explanation_diff) ? q.explanation_diff : [];
  return (
    <li className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid="lms-edit-diff-question" data-status={q.status}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {`Question ${number ?? '?'}${moved ? ` (was ${q.before_ordinal})` : ''}`}
        </span>
        <span className={pillCls(status.tone)}>{status.label}</span>
        {sectionTitle || q.section_id ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">{sectionTitle ? `In ${sectionTitle}` : `In section ${q.section_id}`}</span>
        ) : null}
      </div>
      {fields.length > 0 ? (
        <p className="text-xs text-gray-600 dark:text-gray-300" data-testid="lms-edit-diff-question-fields">
          {`Fields: ${fields.map((f) => FIELD_WORDS[f] ?? f.replace(/_/g, ' ')).join(', ')}`}
        </p>
      ) : null}
      {Array.isArray(q.stem_diff) && q.stem_diff.length > 0 ? (
        <div className="space-y-1">
          <div className={microLabelCls}>Stem</div>
          <WordDiff runs={q.stem_diff} testId="lms-edit-diff-stem" />
        </div>
      ) : null}
      {Array.isArray(q.options_diff) && q.options_diff.length > 0 ? (
        <div className="space-y-1">
          <div className={microLabelCls}>Options and feedback</div>
          <LineDiff lines={q.options_diff} testId="lms-edit-diff-options" />
        </div>
      ) : null}
      {explanation.length > 0 ? (
        <div className="space-y-1">
          <div className={microLabelCls}>Explanation</div>
          <WordDiff runs={explanation} testId="lms-edit-diff-explanation" />
        </div>
      ) : null}
    </li>
  );
}

function BeforeAfter({ before, after }: { before: ReactNode; after: ReactNode }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        <div className={microLabelCls}>Before</div>
        <div className="text-sm text-gray-700 dark:text-gray-300">{before}</div>
      </div>
      <div className="space-y-1">
        <div className={microLabelCls}>After</div>
        <div className="text-sm text-gray-900 dark:text-gray-100">{after}</div>
      </div>
    </div>
  );
}

function StringListView({ items }: { items: unknown }) {
  const list = Array.isArray(items) ? items.map((v) => String(v ?? '')) : [];
  if (list.length === 0) return <span className="text-gray-500 dark:text-gray-400">None</span>;
  return (
    <ol className="list-decimal space-y-0.5 pl-5">
      {list.map((text, i) => (
        <li key={i}>{text}</li>
      ))}
    </ol>
  );
}

function ChangeList<T>({
  changes,
  render,
  keyOf,
}: {
  changes: { added: T[]; removed: T[]; changed: Array<{ key: string; before: T; after: T }> };
  render: (item: T) => string;
  keyOf: (item: T) => string;
}) {
  const none = changes.added.length === 0 && changes.removed.length === 0 && changes.changed.length === 0;
  if (none) return <p className="text-sm text-gray-500 dark:text-gray-400">Only the order changed.</p>;
  return (
    <ul className="space-y-1 text-sm">
      {changes.added.map((item) => (
        <li key={`a-${keyOf(item)}`} className="text-gray-900 dark:text-gray-100" data-change="added">
          <span className="mr-2 text-[10px] font-semibold uppercase tracking-widest text-green-700 dark:text-green-400">Added</span>
          {render(item)}
        </li>
      ))}
      {changes.removed.map((item) => (
        <li key={`r-${keyOf(item)}`} className="text-gray-700 dark:text-gray-300" data-change="removed">
          <span className="mr-2 text-[10px] font-semibold uppercase tracking-widest text-red-700 dark:text-red-400">Removed</span>
          <span className="line-through decoration-red-400">{render(item)}</span>
        </li>
      ))}
      {changes.changed.map((c) => (
        <li key={`c-${c.key}`} className="space-y-0.5 text-gray-900 dark:text-gray-100" data-change="changed">
          <span className="mr-2 text-[10px] font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-400">Changed</span>
          <span className="line-through decoration-red-400 text-gray-600 dark:text-gray-400">{render(c.before)}</span>
          <ArrowRight className="mx-1 inline h-3.5 w-3.5 text-gray-400" aria-label="became" />
          <span>{render(c.after)}</span>
        </li>
      ))}
    </ul>
  );
}

function CourseEntry({ entry, hideLabel = false }: { entry: EditDiffCourseEntry; hideLabel?: boolean }) {
  const label = COURSE_FIELD_LABELS[entry.field] ?? String(entry.field).replace(/_/g, ' ');
  let body: ReactNode;
  switch (entry.field) {
    case 'learning_objectives':
    case 'key_takeaways':
      body = <BeforeAfter before={<StringListView items={entry.before} />} after={<StringListView items={entry.after} />} />;
      break;
    case 'not_covered':
      body = (
        <BeforeAfter
          before={<p className="whitespace-pre-line">{entry.before || 'None'}</p>}
          after={<p className="whitespace-pre-line">{entry.after || 'None'}</p>}
        />
      );
      break;
    case 'glossary':
      body = (
        <ChangeList<GlossaryEntry>
          changes={glossaryChanges(entry.before, entry.after)}
          render={(g) => `${g.term}: ${g.definition}`}
          keyOf={(g) => g.term}
        />
      );
      break;
    case 'competency_checklist':
      body = (
        <ChangeList<ChecklistItem>
          changes={checklistChanges(entry.before, entry.after)}
          render={(c) => c.text}
          keyOf={(c) => c.id}
        />
      );
      break;
    case 'sources':
      body = (
        <ChangeList<CourseSource>
          changes={sourceChanges(entry.before, entry.after)}
          render={(s) => `${s.title}${s.publisher ? ` (${s.publisher})` : ''}`}
          keyOf={(s) => `${s.title}|${s.url ?? ''}`}
        />
      );
      break;
    case 'quiz_settings': {
      const rows = settingChanges(entry.before, entry.after);
      body =
        rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No setting changed.</p>
        ) : (
          <ul className="space-y-0.5 text-sm text-gray-900 dark:text-gray-100">
            {rows.map((r) => (
              <li key={r.key}>
                <span className="font-mono text-xs">{r.key}</span>
                {`: ${r.before} `}
                <ArrowRight className="mx-1 inline h-3.5 w-3.5 text-gray-400" aria-label="became" />
                {` ${r.after}`}
              </li>
            ))}
          </ul>
        );
      break;
    }
    default: {
      const unknown = entry as { before?: unknown; after?: unknown };
      body = (
        <BeforeAfter
          before={<pre className="whitespace-pre-wrap break-words text-xs">{JSON.stringify(unknown.before ?? null, null, 2)}</pre>}
          after={<pre className="whitespace-pre-wrap break-words text-xs">{JSON.stringify(unknown.after ?? null, null, 2)}</pre>}
        />
      );
    }
  }
  return (
    <li className="space-y-2" data-testid="lms-edit-diff-course" data-field={entry.field}>
      {hideLabel ? null : <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</h4>}
      {body}
    </li>
  );
}

export function EditDiffView({ diff }: EditDiffViewProps) {
  const [showSame, setShowSame] = useState(false);
  const sections = Array.isArray(diff.sections) ? diff.sections : [];
  const changedSections = sections.filter((s) => s.status !== 'same');
  const sameCount = sections.length - changedSections.length;
  const shownSections = showSame ? sections : changedSections;
  const titles = new Map(sections.map((s) => [s.id, s.title]));
  const questions = (Array.isArray(diff.questions) ? diff.questions : []) as DiffQuestion[];
  const course = Array.isArray(diff.course) ? diff.course : [];
  const courseFields = course.filter((c) => c.field !== 'sources');
  const sourceEntries = course.filter((c) => c.field === 'sources');
  const swaps = Array.isArray(diff.swaps) ? diff.swaps : [];

  return (
    <div className="space-y-5" data-testid="lms-edit-diff">
      <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-edit-diff-summary">
        {countsSummary(diff)}
      </p>

      {sections.length > 0 ? (
        <section className="space-y-3" aria-label="Sections">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className={subheadingCls}>Sections</h3>
            {sameCount > 0 ? (
              <Checkbox
                label={`Show the ${plural(sameCount, 'unchanged section')}`}
                checked={showSame}
                onChange={setShowSame}
                testId="lms-edit-diff-show-same"
              />
            ) : null}
          </div>
          {shownSections.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No section changed.</p>
          ) : (
            <div className="space-y-3">
              {shownSections.map((s) => (
                <DiffSectionCard key={`${s.id}-${s.status}`} section={s} />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {questions.length > 0 ? (
        <section className="space-y-2" aria-label="Questions" data-testid="lms-edit-diff-questions">
          <h3 className={subheadingCls}>Questions</h3>
          <ul className="space-y-2">
            {questions.map((q, i) => (
              <QuestionCard key={`${q.status}-${q.ordinal ?? 'x'}-${q.before_ordinal ?? 'x'}-${i}`} q={q} sectionTitle={q.section_id ? titles.get(q.section_id) ?? null : null} />
            ))}
          </ul>
        </section>
      ) : null}

      {courseFields.length > 0 ? (
        <section className={`${cardCls} space-y-3`} aria-label="Course level" data-testid="lms-edit-diff-course-level">
          <h3 className={subheadingCls}>Course level</h3>
          <ul className="space-y-4">
            {courseFields.map((entry) => (
              <CourseEntry key={entry.field} entry={entry} />
            ))}
          </ul>
        </section>
      ) : null}

      {swaps.length > 0 ? (
        <section className="space-y-2" aria-label="Wording swaps" data-testid="lms-edit-diff-swaps">
          <h3 className={subheadingCls}>Wording swaps</h3>
          <ul className="space-y-1 text-sm text-gray-900 dark:text-gray-100">
            {swaps.map((s, i) => (
              <li key={`${s.find}-${i}`} className="flex flex-wrap items-center gap-1" data-testid="lms-edit-diff-swap">
                <span className="font-medium">{`"${s.find}"`}</span>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400" aria-label="became" />
                <span className="font-medium">{`"${s.replace}"`}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{`: ${plural(s.count ?? 0, 'place')}`}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {sourceEntries.length > 0 ? (
        <section className={`${cardCls} space-y-3`} aria-label="Sources" data-testid="lms-edit-diff-sources">
          <h3 className={subheadingCls}>Sources</h3>
          <ul className="space-y-4">
            {sourceEntries.map((entry) => (
              <CourseEntry key={entry.field} entry={entry} hideLabel />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default EditDiffView;
